import React, { useState, useEffect } from 'react';
import SVGIcon from '../SVGIcon/SVGIcon';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import './BlogDetails.css';

export default function BlogDetails({ blog, onBack, triggerNotification = () => {} }) {
  const [activeBlog, setActiveBlog] = useState(blog);
  const [comments, setComments] = useState([]);
  const [formName, setFormName] = useState('');
  const [formComment, setFormComment] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');

  const fetchBlogDetails = async () => {
    try {
      const slug = blog.slug || blog.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const res = await axios.get(`${API_BASE_URL}/blogs/${slug}`);
      const data = res.data;
      if (data.blog) {
        setActiveBlog(data.blog);
        setComments(data.blog.comments || []);
      }
    } catch (err) {
      console.warn("Failed to fetch live blog comments, using mock default:", err.message);
      setComments([
        { id: 1, name: "Harish Gowda", date: "May 14, 2026", text: "This post is highly informative! Implementing hyper-local distribution has indeed helped us minimize packaging costs in our cooperative." },
        { id: 2, name: "Meera Nair", date: "April 29, 2026", text: "Azotobacter liquid biofertilizers have worked wonders on our cereal crops. The root development is spectacular." }
      ]);
    }
  };

  useEffect(() => {
    setActiveBlog(blog);
    fetchBlogDetails();

    // Pre-fill commenter name if logged in
    const cachedUser = localStorage.getItem('user');
    if (cachedUser) {
      try {
        const u = JSON.parse(cachedUser);
        setFormName(`${u.first_name} ${u.last_name}`);
      } catch(e) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blog]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!formName.trim() || !formComment.trim()) {
      setSubmitMessage("Please fill in both name and comment.");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      await axios.post(`${API_BASE_URL}/blogs/comments`, {
        post_id: activeBlog.id || blog.id,
        author_name: formName.trim(),
        content: formComment.trim()
      }, { headers });

      setFormComment('');
      setSubmitMessage("Comment submitted successfully!");
      triggerNotification("Comment posted to Knowledge Hub!");

      // Refresh comments list
      fetchBlogDetails();

      setTimeout(() => {
        setSubmitMessage('');
      }, 4000);
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || "Failed to post comment.";
      setSubmitMessage(errorMessage);
    }
  };

  const farmingTips = [
    { title: "Optimize Soil pH", desc: "For organic biofertilizers to work effectively, maintain soil pH between 6.0 and 7.5." },
    { title: "Morning Moisture", desc: "Apply foliar sprays like Super Novel early in the morning for maximum leaf absorption." },
    { title: "Mulching Strategy", desc: "Layer vermicompost underneath organic crop waste mulching to double moisture retention." }
  ];

  const dateObj = activeBlog.created_at ? new Date(activeBlog.created_at) : null;
  const displayDate = dateObj ? dateObj.getDate() : (activeBlog.date || "15");
  const displayMonth = dateObj ? dateObj.toLocaleString('default', { month: 'short' }).toUpperCase() : (activeBlog.month || "JUN");
  const displayYear = dateObj ? dateObj.getFullYear() : "2026";
  const displayImage = activeBlog.image_url || activeBlog.image || "https://images.unsplash.com/photo-1592982537447-6f2a6a0c7c18?w=800&auto=format&fit=crop&q=80";

  return (
    <div className="blog-details-container container animate-scale-up">
      {/* Navigation Header */}
      <div className="details-navigation-header">
        <button className="btn-back-link flex-center" onClick={onBack}>
          <SVGIcon name="arrowLeft" size={16} /> Back to Knowledge Hub
        </button>
        <span className="nav-breadcrumb">
          Home / Knowledge Hub / {activeBlog.category} / Article
        </span>
      </div>

      {/* Main Blog Grid */}
      <div className="blog-main-grid">
        
        {/* Left Side: Article Content */}
        <div className="blog-content-column">
          <div className="blog-article-meta">
            <span className="blog-meta-tag">{activeBlog.category}</span>
            <span className="blog-meta-item">
              <SVGIcon name="user" size={14} /> By Admin
            </span>
            <span className="blog-meta-item">
              💬 {comments.length} Comments
            </span>
            <span className="blog-meta-item font-num">
              📅 {displayDate} {displayMonth} {displayYear}
            </span>
          </div>

          <h1 className="blog-article-title">{activeBlog.title}</h1>

          {/* Large Hero Image */}
          <div className="blog-hero-image-wrap">
            <img src={displayImage} alt={activeBlog.title} className="blog-hero-img" />
          </div>

          {/* Article Text Content */}
          <div className="blog-article-body">
            <p className="lead-paragraph">
              {activeBlog.summary || "Organic farming represents a holistic production management system which promotes and enhances agro-ecosystem health, including biodiversity, biological cycles, and soil biological activity."}
            </p>

            <div
              style={{ whiteSpace: 'pre-wrap' }}
              dangerouslySetInnerHTML={{
                __html: activeBlog.content || "Maintaining a balanced soil environment is critical to yield success. Modern intensive farming often strips the land of trace minerals and microbes. By adding biofertilizers and premium soil conditioners, we inoculate the root system with beneficial micro-flora that naturally synthesize organic compounds, boosting immunity and nutrient uptake."
              }}
            />

            {!activeBlog.content && (
              <>
                <blockquote className="blog-quote">
                  <div className="quote-icon">“</div>
                  <p>Healthy soil is the foundation of the food system. Organic fertilizers don't just feed the plant; they feed the micro-organisms that keep the ecosystem alive.</p>
                  <cite>— Dr. R. K. Sharma, Agronomist</cite>
                </blockquote>

                <h3>The Bio-Security Strategy</h3>
                <p>
                  By utilizing compost feed fortified with Trichoderma viride and Pseudomonas fluorescens, farmers create an active biological shield. Trichoderma viride wraps itself around vulnerable crop roots. Once in place, it secretes enzymes that breakdown the cell walls of harmful soil fungi, acting as a preventative shield against root rots, damping-off, and collar rots.
                </p>

                <h3>Key Benefits of Microbial Boosters</h3>
                <ul>
                  <li><strong>Nitrogen Fixation:</strong> Fixes atmospheric nitrogen directly at root nodes, reducing dependancy on synthetic urea.</li>
                  <li><strong>Potassium Mobilization:</strong> Liberates lock-up potash in clay soils, making it water-soluble and absorbable.</li>
                  <li><strong>Root Architecture:</strong> Humic and fulvic acids stimulate cellular branching, producing longer, thicker secondary roots.</li>
                </ul>

                <p>
                  Transitioning to organic practices requires patience, but the long-term benefits in crop price margins, soil longevity, and environmental health are undeniable. Build your soil organic carbon today, harvest bumper organic yields tomorrow.
                </p>
              </>
            )}
          </div>

          {/* Social Share Bar */}
          <div className="blog-share-row">
            <span>Share this article:</span>
            <div className="share-social-icons" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <a 
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} 
                target="_blank" 
                rel="noopener noreferrer" 
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  triggerNotification("Article link copied! Opening Facebook...");
                }}
                title="Share on Facebook"
              >
                <SVGIcon name="facebook" size={18} className="social-icon" />
              </a>
              <a 
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}`} 
                target="_blank" 
                rel="noopener noreferrer" 
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  triggerNotification("Article link copied! Opening Twitter...");
                }}
                title="Share on Twitter"
              >
                <SVGIcon name="twitter" size={18} className="social-icon" />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  triggerNotification("Article link copied! Opening Instagram...");
                }}
                title="Share on Instagram"
              >
                <SVGIcon name="instagram" size={18} className="social-icon" />
              </a>
            </div>
          </div>

          {/* Comments Section */}
          <section className="blog-comments-section">
            <h2>Comments ({comments.length})</h2>

            {/* Comment Form */}
            <div className="write-comment-container">
              <h3>Leave a Comment</h3>
              {submitMessage && <div className="comment-status-alert">{submitMessage}</div>}
              
              <form onSubmit={handleCommentSubmit} className="comment-form-element">
                <div className="comment-form-group">
                  <label htmlFor="commentName">Your Name</label>
                  <input 
                    type="text" 
                    id="commentName"
                    value={formName} 
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="Enter your name" 
                    required 
                  />
                </div>
                
                <div className="comment-form-group">
                  <label htmlFor="commentText">Comment Message</label>
                  <textarea 
                    id="commentText"
                    rows="4"
                    value={formComment}
                    onChange={(e) => setFormComment(e.target.value)}
                    placeholder="Write your thought, feedback, or questions about this article..."
                    required
                  ></textarea>
                </div>

                <button type="submit" className="btn btn-primary btn-submit-comment">
                  Post Comment
                </button>
              </form>
            </div>

            {/* Comments List */}
            <div className="comments-items-list">
              {comments.map((comment) => (
                <div key={comment.id} className="comment-item-card">
                  <div className="comment-item-header">
                    <span className="commenter-name">{comment.name}</span>
                    <span className="commenter-date">{comment.date}</span>
                  </div>
                  <p className="commenter-text">{comment.text}</p>
                </div>
              ))}
            </div>
          </section>

        </div>

        {/* Right Side: Sidebar */}
        <div className="blog-sidebar-column">
          {/* About Author card */}
          <div className="sidebar-widget author-widget">
            <h3>Orbifert Knowledge</h3>
            <p>Our agricultural specialists publish weekly field research, organic composting recipes, and bio-pest controls to help Indian farmers achieve chemical-free yields.</p>
          </div>

          {/* Farming Tips widget */}
          <div className="sidebar-widget tips-widget">
            <h3>Expert Farming Tips</h3>
            <div className="tips-list">
              {farmingTips.map((tip, idx) => (
                <div key={idx} className="tip-item">
                  <h4>{tip.title}</h4>
                  <p>{tip.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Newsletter sidebar box */}
          <div className="sidebar-widget promo-newsletter-widget">
            <h3>Farming Newsletter</h3>
            <p>Get soil management advice delivered straight to your inbox.</p>
            <input type="email" placeholder="Your email address" className="sidebar-email-input" />
            <button className="btn btn-primary sidebar-subscribe-btn" onClick={() => triggerNotification("Subscribed to farming newsletter!")}>
              Subscribe
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
